import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  FiAlertCircle,
  FiChevronLeft,
  FiChevronRight,
  FiDownload,
  FiExternalLink,
  FiX,
} from "react-icons/fi";
import { obtenerBoletaFirmadaPdf } from "../../services/boletasService";
import { fmtFecha, fmtPeriodo, nombreArchivoBoleta } from "./boletasFormat";

// Los móviles (Android/iOS) normalmente no pueden mostrar PDF dentro de un iframe.
const PUEDE_PREVISUALIZAR =
  typeof navigator === "undefined" || navigator.pdfViewerEnabled !== false;

// Caché compartida: persona+periodo -> Promise<objectURL>. Evita descargar dos
// veces el mismo PDF y permite precargar la siguiente boleta.
export function crearCachePdf() {
  const map = new Map();
  return {
    get(item) {
      const key = `${item.persona}|${item.periodo}`;
      if (!map.has(key)) {
        const p = obtenerBoletaFirmadaPdf(item.persona, item.periodo).then((blob) =>
          URL.createObjectURL(blob)
        );
        p.catch(() => map.delete(key));
        map.set(key, p);
      }
      return map.get(key);
    },
    clear() {
      map.forEach((p) => p.then((u) => URL.revokeObjectURL(u)).catch(() => {}));
      map.clear();
    },
  };
}

export default function BoletaPdfViewer({ items, index, onIndexChange, onClose, cache }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error
  const [blobUrl, setBlobUrl] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const item = items[index];
  const total = items.length;
  const hasPrev = index > 0;
  const hasNext = index < total - 1;
  const titulo = item?.nombreCompleto || item?.persona || "Boleta";

  // Abrir como modal nativo: bloquea el fondo (inert), gestiona el foco y Esc.
  useEffect(() => {
    const dialog = dialogRef.current;
    const prevOverflow = document.body.style.overflow;
    if (dialog && !dialog.open) dialog.showModal();
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();
    return () => {
      document.body.style.overflow = prevOverflow;
      if (dialog?.open) dialog.close();
    };
  }, []);

  useEffect(() => {
    if (!item) return undefined;
    let vigente = true;
    setStatus("loading");
    setErrorMsg("");
    setBlobUrl("");

    cache
      .get(item)
      .then((url) => {
        if (!vigente) return;
        setBlobUrl(url);
        setStatus("ready");
      })
      .catch((err) => {
        if (!vigente) return;
        setErrorMsg(err?.message || "No se pudo cargar el PDF.");
        setStatus("error");
      });

    return () => {
      vigente = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item?.persona, item?.periodo, cache, reloadKey]);

  // Precarga de la siguiente boleta para que la navegación sea inmediata.
  useEffect(() => {
    if (status !== "ready") return;
    const next = items[index + 1];
    if (next) cache.get(next).catch(() => {});
  }, [status, items, index, cache]);

  const goPrev = useCallback(() => hasPrev && onIndexChange(index - 1), [hasPrev, index, onIndexChange]);
  const goNext = useCallback(() => hasNext && onIndexChange(index + 1), [hasNext, index, onIndexChange]);

  const handleKeyDown = (e) => {
    if (e.target instanceof HTMLInputElement) return;
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      goPrev();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      goNext();
    }
  };

  if (!item) return null;

  // Sin blob (cargando o con error), abrir el archivo de uploads/ directamente.
  const openUrl = blobUrl || item.uploadsUrl || item.pdfUrl;

  return (
    <dialog
      ref={dialogRef}
      className="bol-viewer"
      aria-labelledby="bol-viewer-title"
      aria-describedby="bol-viewer-meta"
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Clic en el fondo oscuro (fuera del panel) cierra.
        if (e.target === dialogRef.current) onClose();
      }}
      onKeyDown={handleKeyDown}
    >
      <div className="bol-viewer__panel">
        <header className="bol-viewer__header">
          <div className="bol-viewer__info">
            <h2 id="bol-viewer-title" className="bol-viewer__title">{titulo}</h2>
            <p id="bol-viewer-meta" className="bol-viewer__meta">
              <span>Doc. {item.persona || "-"}</span>
              <span aria-hidden="true">·</span>
              <span>{fmtPeriodo(item.periodo)}</span>
              <span aria-hidden="true">·</span>
              <span>Firmada el {fmtFecha(item.fechaConfirmacion)}</span>
            </p>
          </div>

          <div className="bol-viewer__actions">
            <div className="bol-viewer__nav" role="group" aria-label="Navegar entre boletas">
              <button
                type="button"
                className="bol-icon-btn"
                onClick={goPrev}
                disabled={!hasPrev}
                aria-label="Boleta anterior"
                aria-keyshortcuts="ArrowLeft"
                title="Anterior (←)"
              >
                <FiChevronLeft aria-hidden="true" />
              </button>
              <span className="bol-viewer__pos" aria-live="polite">
                {index + 1} de {total}
              </span>
              <button
                type="button"
                className="bol-icon-btn"
                onClick={goNext}
                disabled={!hasNext}
                aria-label="Boleta siguiente"
                aria-keyshortcuts="ArrowRight"
                title="Siguiente (→)"
              >
                <FiChevronRight aria-hidden="true" />
              </button>
            </div>

            <a
              className={`bol-icon-btn ${status !== "ready" ? "is-disabled" : ""}`}
              href={status === "ready" ? blobUrl : undefined}
              download={nombreArchivoBoleta(item)}
              aria-disabled={status !== "ready"}
              aria-label="Descargar PDF"
              title="Descargar PDF"
            >
              <FiDownload aria-hidden="true" />
            </a>
            <a
              className="bol-icon-btn"
              href={openUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Abrir en una pestaña nueva"
              title="Abrir en una pestaña nueva"
            >
              <FiExternalLink aria-hidden="true" />
            </a>
            <button
              ref={closeBtnRef}
              type="button"
              className="bol-icon-btn bol-icon-btn--close"
              onClick={onClose}
              aria-label="Cerrar vista previa"
              aria-keyshortcuts="Escape"
              title="Cerrar (Esc)"
            >
              <FiX aria-hidden="true" />
            </button>
          </div>
        </header>

        <div className="bol-viewer__body">
          {status === "loading" && (
            <div className="bol-viewer__state" role="status">
              <span className="boletas-spinner" aria-hidden="true" />
              <p>Cargando boleta…</p>
            </div>
          )}

          {status === "error" && (
            <div className="bol-viewer__state" role="alert">
              <FiAlertCircle className="bol-viewer__state-icon is-error" aria-hidden="true" />
              <p><strong>No se pudo mostrar la boleta.</strong></p>
              <p className="boletas-muted">{errorMsg}</p>
              <div className="bol-viewer__state-actions">
                <button type="button" className="boletas-btn boletas-btn--sm" onClick={() => setReloadKey((k) => k + 1)}>
                  Reintentar
                </button>
                <a className="boletas-btn boletas-btn--sm boletas-btn--ghost" href={openUrl} target="_blank" rel="noopener noreferrer">
                  Abrir en pestaña nueva
                </a>
              </div>
            </div>
          )}

          {status === "ready" && !PUEDE_PREVISUALIZAR && (
            <div className="bol-viewer__state">
              <p><strong>Este navegador no puede mostrar el PDF aquí.</strong></p>
              <p className="boletas-muted">Descárguelo o ábralo en una pestaña nueva.</p>
              <div className="bol-viewer__state-actions">
                <a className="boletas-btn boletas-btn--sm" href={blobUrl} download={nombreArchivoBoleta(item)}>
                  <FiDownload aria-hidden="true" /> Descargar
                </a>
                <a className="boletas-btn boletas-btn--sm boletas-btn--ghost" href={blobUrl} target="_blank" rel="noopener noreferrer">
                  <FiExternalLink aria-hidden="true" /> Abrir
                </a>
              </div>
            </div>
          )}

          {status === "ready" && PUEDE_PREVISUALIZAR && (
            <iframe
              key={blobUrl}
              className="bol-viewer__frame"
              src={`${blobUrl}#view=FitH`}
              title={`Boleta de ${titulo}, ${fmtPeriodo(item.periodo)}`}
            />
          )}
        </div>

        <footer className="bol-viewer__footer" aria-hidden="true">
          <kbd>←</kbd> <kbd>→</kbd> navegar · <kbd>Esc</kbd> cerrar
        </footer>
      </div>
    </dialog>
  );
}
